import { Menu } from 'app/shared/models/control/menu';
import { ExternalType } from 'app/shared/models/core/enums/external-type';
import { environment } from 'environments/environment';

/**
 * Ported verbatim from home.component.ts's isExternalLink()/getUrlOrExternalType(), which
 * onAddFavorite() uses to build the FavoritePageStaffService payload. Promoted here so the new
 * flyout/pinned-strip pin buttons build the exact same payload shape instead of a third
 * duplicate implementation. Intentionally NOT reusing MenuNavigationService's broader
 * isExternalLink()/externalUrl() (which also cover isOncologyManagement/isHomeCare for
 * *navigation* as a deliberate superset) - this keeps the *favorites payload* bit-for-bit
 * identical to what home.component.ts has always produced, including its existing gap around
 * isOncologyManagement/isHomeCare, since changing that is out of scope for this redesign.
 */
export function isExternalFavoriteLink(item: Menu): boolean {
    return !!(item.isAppointment || item.inNursingManagement || item.isOperationManagement
        || item.isCpoeManagement || item.isCateringManagement || item.isVatIntegartionManagement
        // Not part of the port - the Tools module's shared dynamic modules are new here, and a
        // favorite saved for one is only openable as an external link: its url resolves in the
        // smart-forms viewer, not in this app's router.
        || item.isDynamicModule);
}

export function getFavoriteUrlOrExternalType(item: Menu, isUrl: boolean = true): number | string {
    if (item.isAppointment) {
        return isUrl ? environment.appointmentURI + item.url : ExternalType.Appointment;
    }
    if (item.inNursingManagement) {
        return isUrl ? environment.nursingManagement + item.url : ExternalType.NursingManagement;
    }
    if (item.isOperationManagement) {
        return isUrl ? environment.operationManagement + item.url : ExternalType.OperationManagement;
    }
    if (item.isCpoeManagement) {
        return isUrl ? environment.cpoeManagement + item.url : ExternalType.CpoeManagement;
    }
    if (item.isCateringManagement) {
        return isUrl ? environment.cateringManagement + item.url : ExternalType.CateringManagement;
    }
    if (item.isVatIntegartionManagement) {
        return isUrl ? environment.VatIntegrationManagement + item.url : ExternalType.VatIntegrationManagement;
    }
    if (item.isHomeCare) {
        return isUrl ? environment.homeCareUrl + item.url : ExternalType.HomeCare;
    }
    if (item.isDynamicModule) {
        return isUrl ? environment.dynamicFormViewer + item.url : ExternalType.DynamicModule;
    }
    return isUrl ? item.url : 0;
}

export function buildFavoritePayload(item: Menu): { Path: string; Title: string; Url: string | number; IsExternalLink: boolean; IsReport: boolean; ExternalType: number | string } {
    return {
        Path: item.path,
        Title: item.title,
        Url: getFavoriteUrlOrExternalType(item),
        IsExternalLink: isExternalFavoriteLink(item),
        IsReport: item.directReport || false,
        ExternalType: getFavoriteUrlOrExternalType(item, false),
    };
}
